import { FormEvent, useEffect, useRef, useState } from 'react';

export default function ContactForm() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector('input[name="website_alt"]') as HTMLInputElement | null;
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setFormStatus('submitting');
    setErrorMsg('');
    const formData = new URLSearchParams();
    const data = new FormData(form);
    data.forEach((value, key) => {
      if (key === 'website_alt') return;
      formData.append(key, value as string);
    });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7f5ip4q2rhf4jj5qeng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
        signal: controller.signal,
      });
      const responseText = await res.text();
      let code = '';
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? '';
        serverMsg = parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? '';
      } catch {
        code = '';
      }
      if (res.ok && code === 'OK') {
        setFormStatus('success');
        setErrorMsg('');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
        setErrorMsg(serverMsg || '送出失敗，請稍後再試，或直接加入 LINE@ 聯絡我。');
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setFormStatus('error');
        setErrorMsg('連線逾時，請稍後再試一次，或直接加入 LINE@ 聯絡我。');
      } else {
        setFormStatus('error');
        setErrorMsg('網路連線異常，請稍後再試，或直接加入 LINE@ 聯絡我。');
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return (
    <section className="py-20 md:py-24 bg-[#f2ede5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="w-6 h-px bg-[#b08060]"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Message</span>
                <div className="w-6 h-px bg-[#b08060]"></div>
              </div>
              <h2
                className="font-serif-tc text-[#2c2420] leading-[1.4]"
                style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
              >
                還沒準備好預約？
              </h2>
              <p className="mt-3 text-[1.0625rem] leading-[1.75] text-[#7a6e62]">
                可以先留下問題。告訴我你的稱呼、聯絡方式與想問的事，我會盡快回覆。
              </p>
            </div>

            {formStatus === 'success' ? (
              <div role="status" aria-live="polite" className="bg-[#faf8f5] rounded-2xl p-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#4a6741]/15 mx-auto mb-6">
                  <i className="ri-checkbox-circle-line text-[#4a6741] text-3xl"></i>
                </div>
                <h3 className="font-serif-tc text-[#2c2420] mb-4" style={{ fontSize: '1.375rem' }}>
                  訊息已送出
                </h3>
                <p className="text-[1.0625rem] leading-[1.75] text-[#7a6e62]">
                  謝謝你願意聯絡鳴箏。我會在回覆時間內盡快與你聯繫，請稍作等待。
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-8 text-[0.9375rem] text-[#7a6248] underline underline-offset-2 cursor-pointer"
                >
                  再送出一則訊息
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-[#faf8f5] rounded-2xl p-8 md:p-10 space-y-6"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-aux"
                />
                <div>
                  <label className="block text-[0.9375rem] font-medium text-[#5a4e42] mb-2" htmlFor="name">
                    你的稱呼 <span className="text-[#9a8470]">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="例：小美、王小明"
                    className="w-full px-4 py-3 rounded-xl border border-[#e0d8cc] bg-white text-[1rem] text-[#3a2e22] placeholder-[#c0b4a0] focus:outline-none focus:border-[#7a6248] transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-[0.9375rem] font-medium text-[#5a4e42] mb-2" htmlFor="contact">
                    聯絡方式 <span className="text-[#9a8470]">*</span>
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    type="text"
                    required
                    placeholder="手機 / LINE ID / Email 皆可"
                    className="w-full px-4 py-3 rounded-xl border border-[#e0d8cc] bg-white text-[1rem] text-[#3a2e22] placeholder-[#c0b4a0] focus:outline-none focus:border-[#7a6248] transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-[0.9375rem] font-medium text-[#5a4e42] mb-2" htmlFor="message">
                    想問的問題 <span className="text-[#9a8470]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="不需要寫太正式，就像跟朋友說話一樣。例如：我完全沒有音樂基礎，想了解成人是否適合學古箏……"
                    className="w-full px-4 py-3 rounded-xl border border-[#e0d8cc] bg-white text-[1rem] text-[#3a2e22] placeholder-[#c0b4a0] focus:outline-none focus:border-[#7a6248] transition-colors resize-none text-sm"
                    onChange={(e) => setCharCount(e.target.value.length)}
                  ></textarea>
                  <div className="flex justify-between mt-1">
                    <span className="text-[0.8125rem] text-[#9a8470]">請保持在 500 字以內</span>
                    <span className={`text-[0.8125rem] ${charCount > 480 ? 'text-[#c0622a]' : 'text-[#9a8470]'}`}>
                      {charCount} / 500
                    </span>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || charCount > 500}
                    className="w-full py-4 rounded-xl bg-[#4a3e32] hover:bg-[#3a2e22] text-[#f5f0e8] text-[1rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formStatus === 'submitting' ? '送出中⋯⋯' : '送出問題'}
                  </button>
                  {formStatus === 'error' && (
                    <p role="alert" className="mt-3 text-center text-[0.9375rem] text-[#c0622a]">
                      {errorMsg || '送出失敗，請稍後再試，或直接加入 LINE@ 聯絡我。'}
                    </p>
                  )}
                  <p className="mt-4 text-center text-[0.9375rem] text-[#9a8470] leading-relaxed">
                    送出後我會盡快回覆。也歡迎直接
                    <a
                      href="https://line.me"
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-[#7a6248] underline underline-offset-2 cursor-pointer"
                    >
                      加入 LINE@
                    </a>
                    ，更即時。
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}